import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ClassroomHandoverPageShell from "../components/handover/ClassroomHandoverPageShell";
import ClassroomSetupForm from "../components/handover/ClassroomSetupForm";
import HandoverRouteState from "../components/handover/HandoverRouteState";
import ConfirmationModal from "../components/ConfirmationModal";
import useClassroomHandoverSetup from "../hooks/useClassroomHandoverSetup";
import useHandoverIdentity from "../hooks/useHandoverIdentity";
import useUnsavedChangesGuard from "../hooks/useUnsavedChangesGuard";
import {
  destinationWithSelection,
  parseClassroomHandoverSelection,
} from "../utils/classroomHandoverSelection";

const ROUTE_STATE_COPY = {
  forbidden: ["This classroom setup isn’t available", "You don’t have access to set up this classroom."],
  not_found: ["Classroom not found", "This setup link may be out of date."],
  complete: ["This classroom is already set up", "Return to My Classes to open the active classroom."],
  error: ["We couldn’t load this setup", "Try again without leaving the page."],
};

function ClassroomHandoverSetup() {
  const { classId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { identity, identityStatus } = useHandoverIdentity();
  const [dirty, setDirty] = useState(false);
  const selection = useMemo(
    () => parseClassroomHandoverSelection(location.search),
    [location.search]
  );
  const returnDestination = destinationWithSelection({
    url: "/classadmindashboard",
    ...selection,
  });
  const setup = useClassroomHandoverSetup({
    schoolId: identity?.schoolid,
    classId,
  });
  const guard = useUnsavedChangesGuard(dirty);
  const returnToClasses = () => navigate(returnDestination);
  const retrySetup = () => setup.load().catch(() => {});

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && !guard.showExitPrompt) navigate(returnDestination);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [guard.showExitPrompt, navigate, returnDestination]);

  const openChildReview = (saved) => {
    const destination = destinationWithSelection({
      url: `/classroom-handover/${saved.classId || classId}/children`,
      ...selection,
    });
    guard.allowNavigation();
    setDirty(false);
    navigate(destination, { state: location.state });
  };

  const renderContent = () => {
    if (identityStatus === "loading" || setup.status === "idle" || setup.status === "loading") {
      return <div className="spinner-container" role="status" aria-label="Loading classroom setup"><div className="big-ball-spinner" /></div>;
    }
    if (identityStatus === "error") {
      return <HandoverRouteState role="alert" title="We couldn’t verify your account"><button type="button" onClick={returnToClasses}>Return to My Classes</button></HandoverRouteState>;
    }
    if (ROUTE_STATE_COPY[setup.status]) {
      const [title, message] = ROUTE_STATE_COPY[setup.status];
      return (
        <HandoverRouteState role={setup.status === "error" ? "alert" : "status"} title={title} message={message}>
          {setup.status === "error" && <button type="button" onClick={retrySetup}>Try again</button>}
          <button type="button" onClick={returnToClasses}>Return to My Classes</button>
        </HandoverRouteState>
      );
    }
    if (setup.status === "conflict") {
      return (
        <HandoverRouteState role="alert" title="This setup changed elsewhere" message="Another person saved a newer version. Review their latest changes before continuing.">
          <button type="button" onClick={setup.resumeLatest} disabled={!setup.conflictEntry}>Resume latest setup</button>
          <button type="button" onClick={returnToClasses}>Return to My Classes</button>
        </HandoverRouteState>
      );
    }
    if (!setup.entry) {
      return (
        <HandoverRouteState role="alert" title="We couldn’t load this setup" message="The classroom setup response was empty. Try loading it again.">
          <button type="button" onClick={retrySetup}>Try again</button>
          <button type="button" onClick={returnToClasses}>Return to My Classes</button>
        </HandoverRouteState>
      );
    }
    return (
      <ClassroomSetupForm
        key={`${setup.entry.classId}-${setup.entry.version}`}
        entry={setup.entry}
        status={setup.status}
        onSearchTeachers={setup.searchTeachers}
        onInviteTeacher={setup.inviteTeacher}
        onContinue={setup.submit}
        onComplete={openChildReview}
        onBack={returnToClasses}
        onClose={returnToClasses}
        onDirtyChange={setDirty}
      />
    );
  };

  return (
    <ClassroomHandoverPageShell identity={identity}>
      {renderContent()}
      <ConfirmationModal
        show={guard.showExitPrompt}
        handleClose={guard.stay}
        handleConfirmChange={guard.exit}
        title="Exit setup?"
        message="Unsaved changes will be lost."
        confirmLabel="Yes, exit"
        returnLabel="Return to setup"
        confirmVariant="outline-primary"
        returnVariant="primary"
        confirmFirst
        centered
      />
    </ClassroomHandoverPageShell>
  );
}

export default ClassroomHandoverSetup;
